import { createRoute, useNavigate } from "@tanstack/solid-router";
import { createSignal, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { rootRoute } from "@/routes/__root";
import { AppBackground } from "@/components/app-background";
import { writeSessionToken } from "@/lib/api";
import { isTauriRuntime } from "@/lib/platform";
import { finishSocialLogin, socialErrorMessage } from "@/lib/social-login";
import { parseSocialCallbackParams, SOCIAL_CALLBACK_PATH } from "@/lib/social-callback-url";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { GlassSurface } from "@fleet/ui";
import { AlertCircle, Loader2 } from "@fleet/icons";

function SocialCallbackPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [failed, setFailed] = createSignal<string | null>(null);

  onMount(() => {
    const params = parseSocialCallbackParams(globalThis.window.location.hash);
    // Clear the fragment so the token does not remain in the address bar.
    globalThis.window.history.replaceState(null, "", globalThis.window.location.pathname);

    if (params.status !== "ok") {
      const code = params.error ?? "unknown";
      setFailed(socialErrorMessage(t, code));
      showToast(socialErrorMessage(t, code), "error");
      // Return to /login after a brief moment so the screen is not left hanging.
      globalThis.setTimeout(() => {
        void navigate({ to: "/login", replace: true });
      }, 2500);
      return;
    }

    // In WebView/Tauri the SameSite=Lax cookie is lost: we persist the token
    // (Bearer). On web auth uses the `mealplan.device` cookie that the server
    // just set in the callback, so the `#token` fragment is ignored.
    // NOTE: native OAuth now returns via an App/Universal Link handled in
    // `deep-linking-provider.tsx` (token stored in Rust, never in JS); this
    // in-WebView `#token` path remains for web + as a Tauri fallback.
    const onTauri = isTauriRuntime();
    if (params.token && onTauri) {
      writeSessionToken(params.token);
    }

    // Tauri only: the opaque session token from the redirect fragment (`#token=…`)
    // is the Bearer it must use (the OAuth account is not in the registry yet, and
    // the `get-session` body never carries the token). Web leaves this undefined —
    // its `get-session` authenticates via the just-set device + session cookie.
    const oauthToken = onTauri ? params.token || undefined : undefined;
    void finishSocialLogin({ navigate, redirect: params.redirect, oauthToken });
  });

  return (
    <>
      {/* Decorative fixed backdrop sibling — NEVER the scroll parent (#13). */}
      <AppBackground />

      {/* Own scroll container so the status card is reachable on short viewports. */}
      <div
        class="relative z-10 flex h-[100dvh] w-full items-center justify-center overflow-x-hidden overflow-y-auto p-4"
        style={{
          "padding-top": "max(1rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1rem, env(safe-area-inset-bottom))",
        }}
      >
        <GlassSurface class="flex w-full max-w-sm flex-col items-center justify-center gap-4 p-12 text-center">
          <Show
            when={failed()}
            fallback={
              <>
                <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
                <p class="text-sm text-muted-foreground">{t(msg`Completing sign-in…`)}</p>
              </>
            }
          >
            {(message) => (
              <>
                <AlertCircle class="h-8 w-8 text-destructive" />
                <p class="text-sm text-destructive">{message()}</p>
                <p class="text-xs text-muted-foreground">{t(msg`Redirecting to sign in…`)}</p>
              </>
            )}
          </Show>
        </GlassSurface>
      </div>
    </>
  );
}

export const socialCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  // CC-24: single source of truth for the path (shared with the NON_PREFIXED_PREFIXES allowlist).
  path: SOCIAL_CALLBACK_PATH,
  component: SocialCallbackPage,
});
